/**
 * Checks if a value is a non-null object and not an array.
 * @param {any} item - The value to check.
 * @returns {boolean} - True if the value is a non-null object and not an array, false otherwise.
 */
function isObject(item: any): boolean {
  return item !== null && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Deeply merges two objects up to the second level.
 * @example
 * const obj1 = { a: 1, b: { c: 2 } };
 * const obj2 = { b: { d: 3 }, e: 4 };
 * const merged = deepMerge(obj1, obj2);
 * console.log(merged); // { a: 1, b: { d: 3 }, e: 4 }
 */
export function deepMerge(target: any, source: any): any {
  if (!isObject(target) || !isObject(source)) {
    return source;
  }

  let result = target;

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (isObject(sourceValue) && isObject(targetValue)) {
        // Recursively perform a deep merge
        if (result === target) {
          result = { ...target };
        }
        result[key] = deepMerge(targetValue, sourceValue);
      } else if (result[key] !== sourceValue) {
        // Directly replace the target value with the source value
        if (result === target) {
          result = { ...target };
        }
        result[key] = sourceValue;
      }
    }
  }

  return result;
}

/**
 * Type definition for an object with any properties.
 */
type AnyObject = { [key: string]: any };

/**
 * Deep merges two objects, with values from the target object taking precedence.
 * Keys present in the first object are not removed from the result.
 * The resulting object maintains the order of keys from the source object.
 * Only keys that exist in the source object at the first level are included in the result.
 *
 * @param {AnyObject} source - The source object.
 * @param {AnyObject} target - The target object.
 * @returns {AnyObject} - The resulting merged object.
 */
export function deepMergeExcludeKeys(
  source: AnyObject,
  target: AnyObject = {},
): AnyObject {
  const result: AnyObject = {};

  // Copy all properties from the source to the result
  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      if (Object.prototype.hasOwnProperty.call(target, key)) {
        // If key exists in both objects, merge them
        if (isObject(source[key]) && isObject(target[key])) {
          result[key] = deepMerge(source[key], target[key]);
        } else {
          // If they are not both objects, the target value takes precedence
          result[key] = target[key];
        }
      } else {
        // If key does not exist in target, just copy the source value
        result[key] = source[key];
      }
    }
  }

  return result;
}
